import React from "react";
import styled, { useTheme } from "styled-components";
import debounce from "lodash.debounce";

import { LONG_DEBOUNCE } from "@config/consts";

import { Margins, TestWrapper } from "@ui/Atoms";
import { Row, Column } from "@ui/Atoms/Grid";
import { Counter, InfoMessage } from "@ui/Molecules";
import { rem } from "@ui/helpers";
import { Shirt } from "@ui/Assets/MyIcons";

import { quarterTime } from "@services/Filters";

interface Props {
  step: number;
  duration: number;
  counterValue?: number;
  minDuration: number;
  maxDuration: number;
  filter?: (value: number) => number;
  handleOnCounterChange: (duration: number, action?: string) => void;
  skipClientInfoColumn?: boolean;
  translations: {
    selectDurationText: string;
    serviceContent1Text: string;
    serviceContent2Text: string;
  };
  testId?: string;
  serviceContentTestId?: string;
  serviceIcon?: React.ReactElement;
}

const ModalContentService = (props: Props): React.ReactElement => {
  const theme = useTheme();
  return (
    <Margins xs={["base_x2", "base_x3", null, null]}>
      <div>
        <TestWrapper testId={props.testId}>
          <Row>
            <Column xs={12} sm={props.skipClientInfoColumn ? 12 : 6}>
              <CounterWrapper>
                {props.serviceIcon ? (
                  <div>{props.serviceIcon}</div>
                ) : (
                  <IconWrapper>
                    <Shirt width={theme.icons.sizes.base_x4} />
                  </IconWrapper>
                )}
                <Margins xs={[null, "base_x2", null, null]}>
                  <span>{props.translations.selectDurationText}</span>
                </Margins>
                <CounterStyled
                  inputId="serviceTime"
                  maxValue={props.maxDuration}
                  minValue={props.minDuration}
                  value={props.counterValue}
                  isInputDisabled
                  step={props.step}
                  autoWidth
                  filter={props.filter || quarterTime}
                  onChange={debounce((duration, action) => {
                    props.handleOnCounterChange(duration, action);
                  }, LONG_DEBOUNCE)}
                  inputCustomWidth="wide"
                />
              </CounterWrapper>
            </Column>
            {!props.skipClientInfoColumn && (
              <SeparatorColumn xs={12} sm={6}>
                <Margins
                  xs={["base_x2", "base_x2", null, null]}
                  sm={["0", "base_x2", null, null]}
                >
                  <div>
                    <LargeInfo>
                      {props.translations.serviceContent1Text}
                    </LargeInfo>
                  </div>
                </Margins>
                <InfoMessage
                  testId={props.serviceContentTestId}
                  message={props.translations.serviceContent2Text}
                />
              </SeparatorColumn>
            )}
          </Row>
        </TestWrapper>
      </div>
    </Margins>
  );
};

export { ModalContentService };

ModalContentService.displayName = "ModalContentService";

const CounterStyled = styled(Counter)`
  max-width: ${rem(212)};
`;

const LargeInfo = styled.h4`
  font-weight: ${props => props.theme.fonts.weights.normal};
  font-size: ${props => props.theme.fonts.sizes.body};
  margin: 0;
`;

const CounterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: ${props => props.theme.margins.base};
`;

//@ts-ignore (weird error here)
const SeparatorColumn = styled(Column)`
  padding: ${props => props.theme.margins.base_x2}
    ${props => props.theme.margins.base_x4};

  ${props => props.theme.breakpoints.tablet} {
    border-left: 1px solid ${props => props.theme.palette.grey100};
  }
`;

const IconWrapper = styled.div`
  margin: ${props => props.theme.margins.half};
`;
